import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";
import { generateInvoice } from "@/utils/invoiceGenerator";
import { toast } from "sonner";

interface OrderItem {
  product_name: string;
  product_price: number;
  quantity: number;
  selected_size?: string;
  selected_pot_type?: string;
  selected_pot_color?: string;
}

interface Order {
  id: string;
  order_number?: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  shipping_address: any;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  payment_status: string;
  order_status: string;
  created_at: string;
  order_items: OrderItem[];
}

interface InvoiceViewerProps {
  order: Order | null;
  open: boolean;
  onClose: () => void;
}

export const InvoiceViewer = ({ order, open, onClose }: InvoiceViewerProps) => {
  if (!order) return null;

  // Use order_number if available, otherwise fallback to last 8 chars of UUID
  const orderDisplayId = order.order_number || order.id.slice(-8).toUpperCase();

  const handleDownload = () => {
    try {
      generateInvoice(order);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getItemVariantText = (item: OrderItem) => {
    const parts = [];
    if (item.selected_size) parts.push(item.selected_size);
    if (item.selected_pot_type) parts.push(item.selected_pot_type);
    if (item.selected_pot_color) parts.push(item.selected_pot_color);
    return parts.length > 0 ? parts.join(' / ') : null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Invoice</DialogTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </DialogHeader>

        <div className="bg-white p-8 rounded-lg border">
          {/* Header */}
          <div className="text-center border-b pb-6 mb-6">
            <h1 className="text-4xl font-bold text-foreground mb-2">INVOICE</h1>
            <p className="text-sm text-muted-foreground">
              Invoice #{orderDisplayId}
            </p>
            <p className="text-sm text-muted-foreground">
              Date: {formatDate(order.created_at)}
            </p>
          </div>

          {/* Company and Customer Info */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-foreground mb-2">From:</h3>
              <p className="font-medium">Paudhify</p>
              <p className="text-sm text-muted-foreground">2nd Floor, WeWork - ELDECO Centre,</p>
              <p className="text-sm text-muted-foreground">Block A, Shivalik Colony, Malviya Nagar,</p>
              <p className="text-sm text-muted-foreground">South Delhi, Delhi, India - 110017</p>
              <p className="text-sm text-muted-foreground mt-1">GSTIN: 07NZPPS8540L1Z4</p>
              <p className="text-sm text-muted-foreground">Email: info@paudhify.com</p>
            </div>
            <div className="text-right">
              <h3 className="font-semibold text-foreground mb-2">Bill To:</h3>
              <p className="font-medium">{order.customer_name}</p>
              <p className="text-sm text-muted-foreground">{order.customer_email}</p>
              {order.customer_phone && (
                <p className="text-sm text-muted-foreground">{order.customer_phone}</p>
              )}
              {order.shipping_address && (
                <>
                  <p className="text-sm text-muted-foreground mt-2">
                    {order.shipping_address.address}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode || order.shipping_address.zip_code}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-semibold">Item</th>
                  <th className="text-center p-3 font-semibold w-20">Qty</th>
                  <th className="text-right p-3 font-semibold w-28">Price</th>
                  <th className="text-right p-3 font-semibold w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.order_items.map((item, index) => {
                  const variantText = getItemVariantText(item);
                  return (
                    <tr key={index} className="border-b">
                      <td className="p-3">
                        <p className="font-medium">{item.product_name}</p>
                        {variantText && (
                          <p className="text-sm text-muted-foreground">
                            {variantText}
                          </p>
                        )}
                      </td>
                      <td className="text-center p-3">{item.quantity}</td>
                      <td className="text-right p-3">₹{item.product_price.toFixed(2)}</td>
                      <td className="text-right p-3">
                        ₹{(item.product_price * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium">₹{order.subtotal.toFixed(2)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount:</span>
                  <span className="font-medium text-green-600">
                    -₹{order.discount_amount.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping:</span>
                <span className="font-medium">₹{order.shipping_cost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax:</span>
                <span className="font-medium">₹{order.tax_amount.toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-semibold text-lg">Total:</span>
                  <span className="font-bold text-lg">₹{order.total_amount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div className="text-center py-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              Payment Status: <span className="font-semibold uppercase">{order.payment_status}</span>
            </p>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 pt-6 border-t">
            <p className="text-sm text-muted-foreground">Thank you for your purchase!</p>
            <p className="text-xs text-muted-foreground mt-1">
              For any queries, contact us at info@paudhify.com
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
